import React from 'react';
import styled from 'styled-components';
import DefaultLayout from '../layouts/DefaultLayout';
import SecondaryCallout from '../components/SecondaryCallout';
import { H3, Reference, Ul } from '../util/typography';
import PageIntroduction from '../components/PageIntroduction';
import { phoneIconGreen } from '../util/icons';
import Subsection from '../components/Subsection';
import FeaturedResource from '../components/FeaturedResource';
import MerckAccessProgramImage from '../assets/patient-support-icons/patient-support-merk-access-program.png';
import MyMerckSupport from '../assets/patient-support-icons/my-merck-support.png';
import doseLogo from '../assets/patient-support-icons/patient-support-dose-exchange.png';
import copayLogo from '../assets/patient-support-icons/patient-support-copay.png';
import accessLogo from '../assets/patient-support-icons/patient-support-accessing-lenvima.png';
import eisaiSupport from '../assets/patient-support-icons/patient-support-eisai.png';
import { respondTo } from '../util/breakpoints';
import Section from '../components/Section';
import * as colors from '../util/colors';
import { jobCodes } from '../util/data/jobCodes';

// SEO INFORMATION
const pageMeta = {
    title: `Patient Support | KEYTRUDA® (pembrolizumab) and LENVIMA® (lenvatinib)`,
    keywords: `keytruda and lenvima financial assistance`,
    description: `Health care professionals can learn about support resources for patients prescribed KEYTRUDA® (pembrolizumab) and LENVIMA® (lenvatinib).`,
    schemaJsonLD: [
        `{"@context":"https://schema.org/","url":"https://www.keytrudalenvimahcp.com/patient-support/","@type":"MedicalAudience","audienceType":"Health Care Professionals","geographicArea":"This site is intended for health care professionals of the United States, its territories, and Puerto Rico."}`,

        `{"@context":"https://schema.org/","url":"https://www.keytrudalenvimahcp.com/patient-support/","@type":"MedicalWebPage","name":"Patient Support","description":"Information and support resources for patients who are prescribed KEYTRUDA® (pembrolizumab) + LENVIMA® (lenvatinib)"}`
    ]
}
const jobCode = jobCodes[0].jobCode;

const PSSubsection = styled(Subsection)`
    ${respondTo.md`
        &:first-child{
            padding-top: 40px;
        }
    `}
    a{
        color:${colors.blueDark} !important;
        &: hover{
        color:${colors.focus} !important;
        }
        
    }
`;

const PSSection = styled(Section)`
        padding-bottom: 60px;
`;

const BreakForSmallerDevices = styled.span`
    text-wrap: wrap;
    white-space: wrap;
    @media (min-width: 360px) {
        text-wrap: nowrap;
        white-space: nowrap;
    }
`;

const CustomizeUL = styled.div`
    .keytrudaColor li::before{
        background-color: ${colors.greenDark};
    }

`
const LenvimaLink = styled.a`
    font-weight: 600;
    color: ${colors.mulberry};
    &:hover {
        color: ${colors.mulberryHover};
    }
`;

const KeytrudaLink = styled.a`
    font-weight: 600;
    color: ${colors.blueDark} !important;
    &:hover {
        color: ${colors.focus} !important;
    }
`;


const PatientSupport = ({ location }) => {
    return (
        <DefaultLayout location={location} name='Patient Support' pageMeta={pageMeta} jobCode={jobCode}>
            <PageIntroduction>
                <H3 as="H2">Information and support resources for patients who are prescribed <BreakForSmallerDevices><keytitle>KEYTRUDA®&nbsp;(pembrolizumab)</keytitle></BreakForSmallerDevices> <b>+</b>  <lentitle>LENVIMA®&nbsp;(lenvatinib)</lentitle></H3>
            </PageIntroduction>
            <SecondaryCallout icon={phoneIconGreen}>
                <p>For questions about access or support, reach out to a Call Center Agent at <KeytrudaLink href='tel:833-866-0789'>833&#8209;866&#8209;0789</KeytrudaLink>.</p>
            </SecondaryCallout>
            <PSSection noIntro>
                <PSSubsection>
                    <FeaturedResource buttonText='Visit The Merck Access Program now' buttonTo='https://www.merckaccessprogram-keytruda.com/' imgSrc={MerckAccessProgramImage} imgAlt=''>
                        <p><keytitle>Merck Access and Support for KEYTRUDA</keytitle></p>
                        <p><strong>The Merck Access Program</strong> can help answer questions about benefit investigations, billing and coding, co-pay assistance for eligible patients, prior authorization process, and more.</p>
                    </FeaturedResource>
                </PSSubsection>
                <PSSubsection>
                    <FeaturedResource imgSrc={MyMerckSupport} noTitle imgAlt='My Merck Support Logo'>
                        
                            <p><keytitle>Supporting Patients Along Their Prescribed Treatment Plan</keytitle></p>
                            <p>My Merck Support is designed to provide your patients who are prescribed KEYTRUDA, and their loved ones, with educational resources, practical tips, and helpful tools during treatment. Through My Merck Support, your patients can get tailored support, including:</p>
                            <CustomizeUL>
                                <Ul className="keytrudaColor">
                                    <li>A dedicated team member who can help address nonmedical concerns and provide one-on-one phone support</li>
                                    <li>A starter kit that includes a treatment journal to help patients track and report their symptoms and a cookbook for meal planning and recipes</li>
                                    <li>Educational resources and practical tips that may help them feel prepared during treatment</li>
                                    <li>Connection to local services to help manage transportation, childcare, and more</li>
                                </Ul>
                            </CustomizeUL>
                                <p>Patients who are prescribed KEYTRUDA can learn more and enroll by calling <KeytrudaLink href='tel:888-556-9675'>888-55-MyMRK (888-556-9675)</KeytrudaLink>, Mon–Fri, <span style={{ fontVariant: "small-caps" }}>8am–8pm</span> ET, or by visiting  <KeytrudaLink href="https://www.mymercksupportteam.com/" className='keytruda' target='_blank'>MyMerckSupportTeam.com</KeytrudaLink>.</p>
                    </FeaturedResource>
                </PSSubsection>
                <PSSubsection>
                    <FeaturedResource imgSrc={eisaiSupport} imgAlt='Eisai Patient Support Logo'>
                        <p><lentitle>Eisai Patient Support</lentitle></p>
                        <div>
                            <p>Eisai Patient Support offers information and resources to help patients get access to LENVIMA, including: Benefit verification, co-pay assistance, the LENVIMA Welcome Kit, the LENVIMA Dose Exchange Program, and the LENVIMA Patient Assistance Program.</p><br />
                            <p>Eligibility criteria apply. For program and eligibility information, please visit <LenvimaLink className='lenvima' href='https://www.eisaipatientsupport.com/lenvima' target='_blank'>www.eisaipatientsupport.com/lenvima</LenvimaLink> or call <LenvimaLink className='lenvima' href='tel:1-866-613-4724'>1&#8209;866&#8209;613&#8209;4724</LenvimaLink>.</p><br />
                            <p>Eisai cannot guarantee payment of any claim. Coding, coverage, and reimbursement may vary significantly by payor, plan, patient, and setting of care. Actual coverage and reimbursement decisions are made by individual payors following the receipt of claims. For additional information, customers should consult with their payors for all relevant coding, reimbursement, and coverage requirements. It is the sole responsibility of the provider to select the proper code and ensure the accuracy of all claims used in seeking reimbursement. All services must be medically appropriate and properly supported in the patient medical record.</p>
                        </div>
                    </FeaturedResource>
                </PSSubsection>
                <PSSubsection>
                    <FeaturedResource imgSrc={doseLogo} imgAlt=''>
                        <p><lentitle>Dose Exchange Program</lentitle></p>
                        <div>
                            <p>For assistance with patients that require a dose reduction, please visit <LenvimaLink className='lenvima' href="https://www.eisaipatientsupport.com/lenvima" target='_blank'>www.eisaipatientsupport.com/lenvima</LenvimaLink>. </p><br />
                        </div>
                    </FeaturedResource>
                </PSSubsection>
                <PSSubsection>
                    <FeaturedResource imgSrc={copayLogo} imgAlt=''>
                        <p><lentitle>LENVIMA Co-Pay Program</lentitle></p>
                        <div>
                            <p>
                                With the LENVIMA Co-Pay Program, eligible commercially insured patients may pay as little as $0 per month.* Annual limits apply. Depending on the insurance plan, patients could have additional financial responsibility. See <LenvimaLink className='lenvima' href="https://www.eisaipatientsupport.com/lenvima" target='_blank'>www.eisaipatientsupport.com/lenvima</LenvimaLink>  for complete terms and conditions.
                            </p><br />
                            <p>
                                For assistance, call <LenvimaLink className='lenvima' href="tel:+18553472448">1&#8209;855&#8209;347&#8209;2448</LenvimaLink> or visit <LenvimaLink className='lenvima' href="http://www.lenvimacopay.com" target='_blank'>www.LENVIMACopay.com</LenvimaLink> to enroll eligible patients.
                            </p><br />
                            <Reference>
                                *Not available to patients enrolled in state or federal healthcare programs, including Medicare, Medigap, VA, DoD, or TRICARE.
                            </Reference>
                        </div>
                    </FeaturedResource>
                </PSSubsection>
                <PSSubsection>
                    <FeaturedResource imgSrc={accessLogo} imgAlt=''>
                        <p><lentitle>Accessing LENVIMA</lentitle></p>
                        <div>
                            <b>Specialty Pharmacies</b><br /><br />
                            <p>
                                LENVIMA is available through certain Specialty Pharmacies, which will mail the medication directly to patients.
                            </p><br />
                            <p>
                                Please visit <LenvimaLink className='lenvima' href="https://www.eisaipatientsupport.com/lenvima" target='_blank'>www.LENVIMASpecialtyPharmacy.com</LenvimaLink>  for a complete list of the mail-order Specialty Pharmacies that dispense LENVIMA.
                            </p>
                        </div>
                        <div>
                            <b>
                                Physician Office/Clinic or Hospital Pharmacies
                            </b><br /><br />
                            <p>
                                LENVIMA can also be dispensed through eligible physician offices, clinics, or hospital pharmacies. Please contact your preferred distributor for more information, including eligibility requirements.
                            </p>
                        </div>
                    </FeaturedResource>
                </PSSubsection>
            </PSSection>
        </DefaultLayout>
    )
};

export default PatientSupport;
